import { Button } from "@progress/kendo-react-buttons";
import { Droppable, SvgIcon } from "@progress/kendo-react-common";
import { checkIcon, plusIcon, xIcon } from "@progress/kendo-svg-icons";
import { useState } from "react";
import type { GetDataConfidenceResponse } from "../../../../contracts";
import type { ScenarioLibraryScenario } from "../../../../models/scenarioLibraryScenario";
// import type { CommercialPlanMetrics } from "../../../../utils/commercialPlanCalculations";
// import { isDefinedNumber } from "../../../../utils/numberValueConversion";
// import RecommendedScenarioVisual from "./recommendedScenarioVisual";
import ScenarioComparisonSummary from "./scenarioComparisonSummary";
import ScenarioMetricsComparisonGrid from "./scenarioMetricsComparisonGrid";
import { getScenarioInformation } from "./scenarioInformation";

type ScenarioComparisonProps = {
    selectedScenarios: (ScenarioLibraryScenario | null)[];
    draggingScenario: ScenarioLibraryScenario | null;
    onScenarioDrop: (slotIndex: number) => void;
    onScenarioRemove: (slotIndex: number) => void;
};

type ScenarioComparisonSlotProps = {
    scenario: ScenarioLibraryScenario | null;
    slotIndex: number;
    draggingScenario: ScenarioLibraryScenario | null;
    onScenarioDrop: (slotIndex: number) => void;
    onScenarioRemove: (slotIndex: number) => void;
};

const getScenarioColor = (slotIndex: number): string =>
    ["#169B6B", "#9225F5", "#365FFF"][slotIndex % 3] ?? "#169B6B";

const clampConfidenceScore = (score: number): number => Math.max(0, Math.min(100, Math.round(score)));

const getConfidenceQualityLabel = (confidenceLevel: string): string => (
    confidenceLevel.toLowerCase().includes("data quality")
        ? confidenceLevel
        : `${confidenceLevel} Data Quality`
);

/* Green recommendation panel temporarily disabled (SA-187).
type RecommendedScenario = {
    scenario: ScenarioLibraryScenario;
    metrics: CommercialPlanMetrics | null;
    recommendationLabel: string;
    primaryMetricLabel: string;
    primaryMetricValue: number | null;
    secondaryMetricLabel: string;
    secondaryMetricValue: number | null;
};

type RecommendationMetric = {
    label: string;
    value: (metrics: CommercialPlanMetrics) => number | null;
    primaryMetricLabel: string;
    secondaryMetricLabel: string;
    secondaryMetricValue: (metrics: CommercialPlanMetrics) => number | null;
};

type ScenarioWithMetrics = {
    scenario: ScenarioLibraryScenario;
    metrics: CommercialPlanMetrics;
};

const recommendationMetrics: RecommendationMetric[] = [
    {
        label: "Highest profit uplift",
        value: metrics => metrics.profitPercentageChange,
        primaryMetricLabel: "profit uplift",
        secondaryMetricLabel: "ROI",
        secondaryMetricValue: metrics => metrics.roi
    },
    {
        label: "Highest revenue uplift",
        value: metrics => metrics.revenuePercentageChange,
        primaryMetricLabel: "revenue uplift",
        secondaryMetricLabel: "ROI",
        secondaryMetricValue: metrics => metrics.roi
    },
    {
        label: "Highest ROI",
        value: metrics => metrics.roi,
        primaryMetricLabel: "ROI",
        secondaryMetricLabel: "revenue uplift",
        secondaryMetricValue: metrics => metrics.revenuePercentageChange
    }
];

const getRecommendedScenario = (selectedScenarios: (ScenarioLibraryScenario | null)[]): RecommendedScenario | null => {
    const scenariosWithMetrics = selectedScenarios
        .filter((scenario): scenario is ScenarioLibraryScenario => scenario !== null)
        .map(scenario => ({ scenario, metrics: getScenarioInformation(scenario.original).metrics }))
        .filter((scenario): scenario is ScenarioWithMetrics => scenario.metrics !== null);

    for (const recommendationMetric of recommendationMetrics) {
        const eligibleScenarios = scenariosWithMetrics.filter(({ metrics }) => isDefinedNumber(recommendationMetric.value(metrics)));

        if (eligibleScenarios.length > 0) {
            const recommended = eligibleScenarios.reduce((highest, current) => (
                (recommendationMetric.value(current.metrics) ?? Number.NEGATIVE_INFINITY)
                > (recommendationMetric.value(highest.metrics) ?? Number.NEGATIVE_INFINITY)
                    ? current
                    : highest
            ));

            return {
                ...recommended,
                recommendationLabel: recommendationMetric.label,
                primaryMetricLabel: recommendationMetric.primaryMetricLabel,
                primaryMetricValue: recommendationMetric.value(recommended.metrics),
                secondaryMetricLabel: recommendationMetric.secondaryMetricLabel,
                secondaryMetricValue: recommendationMetric.secondaryMetricValue(recommended.metrics)
            };
        }
    }

    return null;
};

const getConfidenceScore = (scenario: ScenarioLibraryScenario): number | null => {
    const confidence = scenario.original.confidence as GetDataConfidenceResponse | null | undefined;

    return confidence ? clampConfidenceScore(confidence.overall_score) : null;
};

*/

const ScenarioComparisonSlot: React.FC<ScenarioComparisonSlotProps> = ({ scenario, slotIndex, draggingScenario, onScenarioDrop, onScenarioRemove }) => {
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const isValidDropTarget: boolean = draggingScenario !== null && draggingScenario.id !== scenario?.id;
    const isDropActive: boolean = isValidDropTarget && isHovering;
    const emptySlotStyles = isDropActive
        ? {
                backgroundColor: "#dbeafe",
                borderColor: "#0d6efd",
                color: "#084298"
            }
        : isValidDropTarget
            ? {
                    backgroundColor: "#ecfdf5",
                    borderColor: "#86efac",
                    color: "#047857"
                }
            : {
                    backgroundColor: "#ffffff",
                    borderColor: "#cbd5e1",
                    color: "#64748b"
                };
    const occupiedSlotStyles = isDropActive
        ? {
                backgroundColor: "#eff6ff",
                borderColor: "#0d6efd"
            }
        : isValidDropTarget
            ? {
                    backgroundColor: "#f0fdf4",
                    borderColor: "#22c55e"
                }
            : {
                    backgroundColor: "#ffffff",
                    borderColor: "#dce4ef"
                };

    const scenarioInfo = getScenarioInformation(scenario?.original);
    const confidence = scenario?.original.confidence as GetDataConfidenceResponse | null | undefined;
    const confidenceScore = confidence ? clampConfidenceScore(confidence.overall_score) : null;
    const hasWarnings = (confidence?.warnings.length ?? 0) > 0;

    return (
        <Droppable
            onDragEnter={() => setIsHovering(true)}
            onDragLeave={() => setIsHovering(false)}
            onDrop={() => {
                if (isValidDropTarget) {
                    onScenarioDrop(slotIndex);
                }
                setIsHovering(false);
            }}
        >
            <div
                className="d-flex flex-column rounded-4 flex-fill h-100"
                style={{
                    minWidth: 0,
                    minHeight: "360px",
                    border: `2px dashed ${scenario ? occupiedSlotStyles.borderColor : emptySlotStyles.borderColor}`,
                    backgroundColor: scenario ? occupiedSlotStyles.backgroundColor : emptySlotStyles.backgroundColor,
                    color: emptySlotStyles.color,
                    transition: "background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease",
                    boxShadow: isDropActive ? "0 0 0 0.25rem rgba(13, 110, 253, 0.18)" : "none"
                }}
            >
                {scenario
                    ? (
                            <div className="d-flex flex-column h-100 p-3 text-start" style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                                <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                    <div style={{ minWidth: 0 }}>
                                        <p className="small text-secondary mb-1">
                                            Scenario
                                            {" "}
                                            {String.fromCharCode(65 + slotIndex)}
                                        </p>
                                        <h2 className="h6 fw-bold text-body mb-0">{scenario.title}</h2>
                                    </div>
                                    <Button
                                        type="button"
                                        fillMode="flat"
                                        size="small"
                                        svgIcon={xIcon}
                                        aria-label={`Remove ${scenario.title}`}
                                        onClick={() => onScenarioRemove(slotIndex)}
                                    />
                                </div>

                                <div className="d-flex align-items-center gap-2 small text-secondary mb-3">
                                    <span>{scenario.retailer}</span>
                                    <span aria-hidden="true">•</span>
                                    <span>{scenario.date}</span>
                                </div>

                                <span
                                    className="badge rounded-3 align-self-start text-wrap text-start px-3 py-2 mb-3"
                                    style={{
                                        backgroundColor: getScenarioColor(slotIndex),
                                        lineHeight: 1.45,
                                        maxWidth: "100%",
                                        overflowWrap: "anywhere"
                                    }}
                                >
                                    {scenario.objective}
                                </span>
                                {confidence && confidenceScore !== null && (
                                    <div className="border-top border-bottom py-3 mb-3">
                                        <p className="small fw-semibold text-secondary mb-2" style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                                            CONFIDENCE
                                        </p>
                                        <div className="d-flex flex-wrap align-items-center gap-2 small">
                                            <span className="badge rounded-pill border border-success-subtle bg-success-subtle text-success-emphasis fw-normal px-2 py-1">
                                                {confidenceScore}
                                                %
                                            </span>
                                            <span className="text-secondary" aria-hidden="true">•</span>
                                            <span className="badge rounded-pill border border-primary-subtle bg-primary-subtle text-primary-emphasis fw-normal px-2 py-1">
                                                {getConfidenceQualityLabel(confidence.confidence_level)}
                                            </span>
                                            <span className="text-secondary" aria-hidden="true">•</span>
                                            <span className={`badge rounded-pill border fw-normal px-2 py-1 ${hasWarnings ? "border-warning-subtle bg-warning-subtle text-warning-emphasis" : "border-success-subtle bg-success-subtle text-success-emphasis"}`}>
                                                {hasWarnings ? `${confidence.warnings.length} ${confidence.warnings.length === 1 ? "Warning" : "Warnings"}` : "Low Risk"}
                                            </span>
                                        </div>
                                    </div>
                                )}
                                <ul className="list-unstyled mb-0">
                                    {scenarioInfo.key_commercial_decisions.length > 0 && (
                                        <p
                                            className="small fw-semibold text-secondary mb-2"
                                            style={{ fontSize: "0.75rem", letterSpacing: "0.04em" }}
                                        >
                                            KEY DECISIONS
                                        </p>
                                    )}
                                    {scenarioInfo.key_commercial_decisions.map((decision: string, index: number) => (
                                        <li key={`${index}-${decision}`} className="d-flex align-items-start gap-2 mb-2">
                                            <SvgIcon icon={checkIcon} className="text-success flex-shrink-0 mt-1" />
                                            <span>{decision}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="row g-2 mb-3">
                                    {scenario.metrics.map(metric => (
                                        <div className="col-4" key={`${scenario.id}-${metric.label}`}>
                                            <div className="rounded-3 bg-body-tertiary p-2 h-100">
                                                <div className="small text-secondary mb-1" style={{ fontSize: "12px" }}>{metric.label}</div>
                                                <div className="fw-semibold" style={{ color: "#006a57", fontSize: "13px" }}>{metric.value}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="d-flex align-items-center justify-content-between gap-2 mt-auto pt-3 border-top">
                                    <Button
                                        type="button"
                                        fillMode="flat"
                                        className="p-0 text-success"
                                    >
                                        View Details
                                    </Button>
                                    <Button
                                        type="button"
                                        fillMode="flat"
                                        className="p-0 text-secondary"
                                        onClick={() => onScenarioRemove(slotIndex)}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            </div>
                        )
                    : (
                            <div className="d-flex flex-column align-items-center justify-content-center text-center h-100 p-3">
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                                    style={{
                                        width: "80px",
                                        height: "80px",
                                        backgroundColor: isDropActive ? "#bfdbfe" : isValidDropTarget ? "#d1fae5" : "#f8fafc",
                                        color: isDropActive ? "#0d6efd" : isValidDropTarget ? "#059669" : "#94a3b8"
                                    }}
                                >
                                    <SvgIcon icon={plusIcon} style={{ width: "34px", height: "34px" }} />
                                </span>
                                <h2 className="h6 fw-bold mb-3" style={{ color: isValidDropTarget ? "#047857" : "#475569" }}>
                                    Scenario
                                    {" "}
                                    {String.fromCharCode(65 + slotIndex)}
                                </h2>
                                <p className="mb-0 small" style={{ color: isValidDropTarget ? "#059669" : "#94a3b8" }}>
                                    {isDropActive ? "Release to add scenario" : "Drag scenario from library"}
                                </p>
                            </div>
                        )}
            </div>
        </Droppable>
    );
};

const ScenarioComparison: React.FC<ScenarioComparisonProps> = ({ selectedScenarios, draggingScenario, onScenarioDrop, onScenarioRemove }) => {
    const selectedCount: number = selectedScenarios.filter(Boolean).length;
    // const recommendedScenario = getRecommendedScenario(selectedScenarios);

    return (
        <section className="h-100 w-100 d-flex flex-column bg-body-tertiary px-3 py-3 overflow-hidden">
            <header className="d-flex flex-column flex-xl-row align-items-xl-start justify-content-between gap-2 mb-4 flex-shrink-0">
                <div>
                    <h1 className="h4 fw-bold text-body mb-2">Scenario Comparison</h1>
                    <p className="mb-0 text-body-secondary" style={{ fontSize: "0.92rem", lineHeight: 1.45 }}>
                        Drag scenarios from the library into the slots below to compare commercial outcomes side-by-side
                    </p>
                </div>
                <span className="text-secondary-emphasis small text-xl-end flex-shrink-0 pt-xl-1">
                    {selectedCount}
                    {" "}
                    of 3 scenarios selected
                </span>
            </header>

            {/* selectedCount >= 2 && recommendedScenario && (
                <RecommendedScenarioVisual
                    title={recommendedScenario.scenario.title}
                    recommendationLabel={recommendedScenario.recommendationLabel}
                    primaryMetricLabel={recommendedScenario.primaryMetricLabel}
                    primaryMetricValue={recommendedScenario.primaryMetricValue}
                    secondaryMetricLabel={recommendedScenario.secondaryMetricLabel}
                    secondaryMetricValue={recommendedScenario.secondaryMetricValue}
                    confidenceScore={getConfidenceScore(recommendedScenario.scenario)}
                />
            ) */}

            <ScenarioComparisonSummary scenarios={selectedScenarios} />

            <div className="d-flex flex-column gap-3 flex-grow-1 overflow-auto pb-1">
                <div
                    className="d-grid gap-3 flex-shrink-0"
                    style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
                >
                    {selectedScenarios.map((scenario, index) => (
                        <ScenarioComparisonSlot
                            key={index}
                            scenario={scenario}
                            slotIndex={index}
                            draggingScenario={draggingScenario}
                            onScenarioDrop={onScenarioDrop}
                            onScenarioRemove={onScenarioRemove}
                        />
                    ))}
                </div>

                <ScenarioMetricsComparisonGrid selectedScenarios={selectedScenarios} />
            </div>
        </section>
    );
};

export default ScenarioComparison;
